import { getCompanyCodeFromToken } from "./authService";
import { API_URL } from "./apiConfig";

const companyCode = getCompanyCodeFromToken();

// ============================================================================
// TABLE-DRIVEN HIRING-REQUEST APPROVAL FLOW
// ----------------------------------------------------------------------------
// Mirrors the candidate approval-flow contract (see candidateApi.getCandidateApprovalFlow
// and approvalApi.js). Backend must expose the endpoints below against two tables:
//   - RequestApprovalLevels   (template: which roles approve, in what order, per request_type)
//   - RequestApprovalProgress (runtime state per request_id, replaces the 3 bit columns)
// See BACKEND_SPEC.md for DDL + endpoint contracts.
// ============================================================================

// --- Runtime flow for a single request -------------------------------------
// Returns [] when no active levels apply to this request (=> no approval required).
// Each step: { level_order, level_name, role_name, approval_status, approver_name, approved_date, notes }
export const getRequestApprovalFlow = async (requestId) => {
  try {
    const response = await fetch(`${API_URL}Approvals/request/${requestId}/approval-flow`);
    if (!response.ok) throw new Error("Failed to fetch request approval flow");
    return await response.json();
  } catch (error) {
    console.error("Error fetching request approval flow:", error);
    return [];
  }
};

// --- Record an approve / reject decision on the current step ----------------
// body: { request_id, level_order, action: "Approved" | "Rejected",
//         approver_name, approver_user_id, notes }
export const submitRequestApprovalAction = async (payload) => {
  try {
    const body = { ...payload, companyCode };
    const response = await fetch(`${API_URL}Approvals/SubmitRequestApprovalAction`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.ok) return await response.json();
    throw new Error("Failed to submit request approval action");
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

// --- Config CRUD (admin: define the approval template) ----------------------
export const fetchRequestApprovalLevels = async () => {
  try {
    const response = await fetch(`${API_URL}Approvals/GetRequestApprovalLevelsAsync/${companyCode}`);
    if (!response.ok) throw new Error("Failed to fetch request approval levels");
    return await response.json();
  } catch (error) {
    console.error("Error fetching request approval levels:", error);
    return [];
  }
};

export const addRequestApprovalLevel = async (newLevel) => {
  try {
    const body = { ...newLevel, compCode: companyCode };
    const response = await fetch(`${API_URL}Approvals/AddRequestApprovalLevel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const updateRequestApprovalLevel = async (level) => {
  try {
    const response = await fetch(`${API_URL}Approvals/UpdateRequestApprovalLevelAsync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(level),
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const toggleRequestApprovalLevelStatus = async (requestApprovalLevelId, isActive) => {
  try {
    const response = await fetch(
      `${API_URL}Approvals/ToggleRequestApprovalLevelStatus/${requestApprovalLevelId}/${isActive}`,
      { method: "POST" }
    );
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};
